import { useSearchParams } from 'react-router-dom'
import { Header, Footer } from '@/components/layout'
import { Card } from '@/components/ui'

export default function Home() {
  const [searchParams] = useSearchParams()
  const isSuccess = searchParams.get('success') === 'true'
  const authCode = searchParams.get('authCode')

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 relative">
        {/* Hero Section with Background Image */}
        <div className="relative min-h-[80vh] flex items-center justify-center">
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <picture className="absolute inset-0">
              <source
                media="(min-width: 1200px)"
                srcSet="https://nestrentalapartments.com/optimized-images/Paris-Londres/l-50/proyecto-londres-alta-pb-14-large.webp"
                type="image/webp"
              />
              <source
                media="(min-width: 1200px)"
                srcSet="https://nestrentalapartments.com/optimized-images/Paris-Londres/l-50/proyecto-londres-alta-pb-14-large.jpg"
                type="image/jpeg"
              />
              <source
                srcSet="https://nestrentalapartments.com/optimized-images/Paris-Londres/l-50/proyecto-londres-alta-pb-14-medium.webp"
                type="image/webp"
              />
              <source
                srcSet="https://nestrentalapartments.com/optimized-images/Paris-Londres/l-50/proyecto-londres-alta-pb-14-medium.jpg"
                type="image/jpeg"
              />
              <img
                alt="NEST Paris Londres"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
                src="https://nestrentalapartments.com/optimized-images/Paris-Londres/l-50/proyecto-londres-alta-pb-14-large.jpg"
              />
            </picture>
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-neutral-900/30 via-transparent to-neutral-900/60" />
          </div>

          {/* Content */}
          <div className="relative z-10 w-full max-w-2xl mx-auto px-4 py-16">
            <div className="fade-in text-center">
              {/* Logo and Title */}
              <div className="mb-8">
                <h1 className="text-white mb-4">NEST Pay</h1>
                <p className="text-lg text-white/80">
                  {isSuccess ? 'Pago Confirmado' : 'Secure Payment Portal'}
                </p>
              </div>

              {isSuccess ? (
                /* Success Card */
                <Card variant="elevated" padding="lg" className="bg-white/95 backdrop-blur-sm">
                  <div className="py-8">
                    {/* Success Icon */}
                    <div className="w-24 h-24 mx-auto mb-6 bg-[#2D5016]/10 rounded-full flex items-center justify-center">
                      <svg
                        className="w-14 h-14 text-[#2D5016]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>

                    {/* Thank You Message */}
                    <h2 className="font-serif text-3xl text-[#2D5016] mb-3">
                      Muchas Gracias!
                    </h2>
                    <p className="text-xl text-neutral-700 mb-6">
                      Tu pago ha sido procesado exitosamente
                    </p>

                    {/* Auth Code */}
                    {authCode && (
                      <div className="p-4 bg-[#2D5016]/5 border border-[#2D5016]/20 inline-block mb-6">
                        <p className="text-sm text-neutral-500 mb-1 uppercase tracking-wide">
                          Codigo de Autorizacion
                        </p>
                        <p className="font-mono text-2xl text-[#2D5016] font-semibold">
                          {authCode}
                        </p>
                      </div>
                    )}

                    <p className="text-neutral-600 max-w-md mx-auto mb-6">
                      Hemos recibido tu pago correctamente. Recibiras un correo
                      de confirmacion con los detalles de tu transaccion.
                    </p>

                    {/* Property Info */}
                    <div className="p-4 bg-neutral-100 inline-block">
                      <p className="font-serif text-lg text-neutral-900">
                        NEST Rental Apartments
                      </p>
                      <p className="text-sm text-neutral-600">
                        Te esperamos pronto!
                      </p>
                    </div>
                  </div>
                </Card>
              ) : (
                /* Default Welcome Card */
                <Card variant="elevated" padding="lg" className="bg-white/95 backdrop-blur-sm">
                  <div className="py-8">
                    {/* Icon */}
                    <div className="w-20 h-20 mx-auto mb-6 bg-neutral-100 flex items-center justify-center">
                      <svg
                        className="w-10 h-10 text-neutral-900"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                    </div>

                    {/* Message */}
                    <h3 className="text-neutral-900 mb-3">Welcome to NEST Pay</h3>
                    <p className="text-neutral-600 max-w-md mx-auto mb-6">
                      This is the secure payment portal for NEST Rental Apartments
                      reservations. If you have received a payment link, please use
                      that link to complete your payment.
                    </p>

                    {/* Property Info */}
                    <div className="p-4 bg-neutral-100 inline-block">
                      <p className="font-serif text-lg text-neutral-900">
                        NEST Rental Apartments
                      </p>
                      <p className="text-sm text-neutral-600">
                        Santiago, Chile
                      </p>
                    </div>
                  </div>
                </Card>
              )}

              {/* Contact */}
              <div className="mt-8 text-sm text-white/70">
                {isSuccess ? (
                  <p>
                    Tienes alguna pregunta?{' '}
                    <a
                      href="mailto:reservas@nestrentalapartments.com"
                      className="text-white hover:underline"
                    >
                      reservas@nestrentalapartments.com
                    </a>
                  </p>
                ) : (
                  <>
                    <p className="mb-2">Don't have a payment link?</p>
                    <p>
                      Please contact us at{' '}
                      <a
                        href="mailto:reservas@nestrentalapartments.com"
                        className="text-white hover:underline"
                      >
                        reservas@nestrentalapartments.com
                      </a>
                    </p>
                  </>
                )}
              </div>

              {/* Trust Badges */}
              <div className="mt-8 flex items-center justify-center gap-6 text-white/60">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                  <span className="text-sm uppercase tracking-wide">SSL Secured</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm">Powered by</span>
                  <span className="font-medium text-white">Transbank WebPay</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}
